import { createElement, useId, useRef, useState, type KeyboardEvent } from 'react';
import dayjs, { type Dayjs } from 'dayjs';

/**
 * `<mat-calendar>`: the month, year and multi-year views of Angular Material's datepicker, with the very same DOM and
 * class names (`mat-calendar-header`, `mat-calendar-table`, `mat-calendar-body-cell`, ...), so the vendored Material
 * datepicker styles apply unchanged. Weeks start on Monday (en-GB, like the app's locale).
 */

const YEARS_PER_PAGE = 24;
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

type View = 'month' | 'year' | 'multi-year';

interface Cell {
  value: number;
  text: string;
  ariaLabel: string;
  disabled: boolean;
  selected: boolean;
  today: boolean;
  active: boolean;
}

const euclideanModulo = (a: number, b: number) => ((a % b) + b) % b;

/** First year of the 24-year page containing `year` (Material's `getStartingYear` with no min/max date). */
const pageStart = (year: number) => year - euclideanModulo(year, YEARS_PER_PAGE);

function CalendarBody({
  label,
  rows,
  numCols,
  firstRowOffset = 0,
  labelMinRequiredCells,
  cellAspectRatio,
  onSelect,
  onKeyDown,
}: {
  label: string;
  rows: Cell[][];
  numCols: number;
  firstRowOffset?: number;
  labelMinRequiredCells: number;
  cellAspectRatio: number;
  onSelect: (cell: Cell) => void;
  onKeyDown: (event: KeyboardEvent) => void;
}) {
  const padding = `${(50 * cellAspectRatio) / numCols}%`;
  return createElement(
    'tbody',
    { className: 'mat-calendar-body', 'mat-calendar-body': '' },
    firstRowOffset < labelMinRequiredCells && (
      <tr aria-hidden="true">
        <td
          className="mat-calendar-body-label"
          colSpan={numCols}
          style={{ paddingTop: padding, paddingBottom: padding }}
        >
          {label}
        </td>
      </tr>
    ),
    rows.map((row, rowIndex) => (
      <tr key={rowIndex} role="row">
        {rowIndex === 0 && firstRowOffset > 0 && (
          <td
            className="mat-calendar-body-label"
            colSpan={firstRowOffset}
            style={{ paddingTop: padding, paddingBottom: padding }}
          >
            {firstRowOffset >= labelMinRequiredCells ? label : ''}
          </td>
        )}
        {row.map((cell) => (
          <td
            key={cell.value}
            role="gridcell"
            className="mat-calendar-body-cell-container"
            style={{ width: `${100 / numCols}%`, paddingTop: padding, paddingBottom: padding }}
          >
            <button
              type="button"
              className={`mat-calendar-body-cell${cell.disabled ? ' mat-calendar-body-disabled' : ''}${cell.active ? ' mat-calendar-body-active' : ''}`}
              tabIndex={cell.active ? 0 : -1}
              disabled={cell.disabled}
              aria-label={cell.ariaLabel}
              aria-pressed={cell.selected}
              aria-current={cell.today ? 'date' : undefined}
              onClick={() => onSelect(cell)}
              onKeyDown={onKeyDown}
            >
              <span
                className={`mat-calendar-body-cell-content mat-focus-indicator${cell.selected ? ' mat-calendar-body-selected' : ''}${cell.today ? ' mat-calendar-body-today' : ''}`}
              >
                {cell.text}
              </span>
              <span aria-hidden="true" className="mat-calendar-body-cell-preview"></span>
            </button>
          </td>
        ))}
      </tr>
    )),
  );
}

export interface MatCalendarProps {
  /** The selected date. */
  selected: Dayjs | null;
  onSelect: (date: Dayjs) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  /** Header label of the month view: `MMM YYYY` (Material default) or `YYYY` (the screens that override it). */
  monthYearLabel?: string;
  /** No year is ever offered: the header's month/year button no longer opens the year/multi-year views. */
  yearless?: boolean;
}

export function MatCalendar({
  selected,
  onSelect,
  minDate,
  maxDate,
  monthYearLabel = 'MMM YYYY',
  yearless = false,
}: MatCalendarProps) {
  const id = useId();
  const [view, setView] = useState<View>('month');
  const [active, setActive] = useState<Dayjs>(() => (selected ?? dayjs()).startOf('day'));
  const focusRoot = useRef<HTMLDivElement>(null);
  const today = dayjs().startOf('day');

  const inRange = (date: Dayjs, unit: 'day' | 'month' | 'year') =>
    (!minDate || !date.endOf(unit).isBefore(minDate.startOf('day'))) &&
    (!maxDate || !date.startOf(unit).isAfter(maxDate.endOf('day')));

  const move = (amount: number, unit: 'day' | 'month' | 'year') => setActive((current) => current.add(amount, unit));

  const periodLabel =
    view === 'month'
      ? active.format(monthYearLabel)
      : view === 'year'
        ? active.format('YYYY')
        : `${pageStart(active.year())} – ${pageStart(active.year()) + YEARS_PER_PAGE - 1}`;

  const previous = () => (view === 'month' ? move(-1, 'month') : move(view === 'year' ? -1 : -YEARS_PER_PAGE, 'year'));
  const next = () => (view === 'month' ? move(1, 'month') : move(view === 'year' ? 1 : YEARS_PER_PAGE, 'year'));
  const previousLabel = { month: 'Previous month', year: 'Previous year', 'multi-year': 'Previous 24 years' }[view];
  const nextLabel = { month: 'Next month', year: 'Next year', 'multi-year': 'Next 24 years' }[view];

  const onKeyDown = (event: KeyboardEvent) => {
    const step: Record<string, [number, 'day' | 'month' | 'year']> =
      view === 'month'
        ? {
            ArrowLeft: [-1, 'day'],
            ArrowRight: [1, 'day'],
            ArrowUp: [-7, 'day'],
            ArrowDown: [7, 'day'],
            PageUp: [-1, 'month'],
            PageDown: [1, 'month'],
          }
        : view === 'year'
          ? {
              ArrowLeft: [-1, 'month'],
              ArrowRight: [1, 'month'],
              ArrowUp: [-4, 'month'],
              ArrowDown: [4, 'month'],
              PageUp: [-1, 'year'],
              PageDown: [1, 'year'],
            }
          : {
              ArrowLeft: [-1, 'year'],
              ArrowRight: [1, 'year'],
              ArrowUp: [-4, 'year'],
              ArrowDown: [4, 'year'],
              PageUp: [-YEARS_PER_PAGE, 'year'],
              PageDown: [YEARS_PER_PAGE, 'year'],
            };
    const found = step[event.key];
    if (found) {
      event.preventDefault();
      move(found[0], found[1]);
    }
  };

  const monthCells = (): { rows: Cell[][]; offset: number } => {
    const first = active.startOf('month');
    const offset = euclideanModulo(first.day() - 1, 7);
    const days = first.daysInMonth();
    const rows: Cell[][] = [[]];
    let column = offset;
    for (let day = 1; day <= days; day += 1) {
      if (column === 7) {
        rows.push([]);
        column = 0;
      }
      const date = first.date(day);
      rows[rows.length - 1].push({
        value: day,
        text: String(day),
        ariaLabel: date.format('D MMMM YYYY'),
        disabled: !inRange(date, 'day'),
        selected: !!selected && selected.isSame(date, 'day'),
        today: today.isSame(date, 'day'),
        active: active.isSame(date, 'day'),
      });
      column += 1;
    }
    return { rows, offset };
  };

  const yearCells = (): Cell[][] =>
    [0, 1, 2].map((r) =>
      [0, 1, 2, 3].map((c) => {
        const month = r * 4 + c;
        const date = active.month(month).startOf('month');
        return {
          value: month,
          text: MONTHS_SHORT[month],
          ariaLabel: date.format('MMMM YYYY'),
          disabled: !inRange(date, 'month'),
          selected: !!selected && selected.isSame(date, 'month'),
          today: today.isSame(date, 'month'),
          active: active.month() === month,
        };
      }),
    );

  const multiYearCells = (): Cell[][] => {
    const start = pageStart(active.year());
    return Array.from({ length: YEARS_PER_PAGE / 4 }, (_, r) =>
      [0, 1, 2, 3].map((c) => {
        const year = start + r * 4 + c;
        const date = active.year(year).startOf('year');
        return {
          value: year,
          text: String(year),
          ariaLabel: String(year),
          disabled: !inRange(date, 'year'),
          selected: !!selected && selected.year() === year,
          today: today.year() === year,
          active: active.year() === year,
        };
      }),
    );
  };

  const month = view === 'month' ? monthCells() : null;

  return createElement(
    'mat-calendar',
    { className: 'mat-calendar', id },
    createElement(
      'mat-calendar-header',
      {},
      <div className="mat-calendar-header">
        <div className="mat-calendar-controls">
          {yearless ? (
            <span className="mat-calendar-period-button mat-calendar-period-label" aria-live="polite">
              <span className="mdc-button__label">{periodLabel}</span>
            </span>
          ) : (
            <button
              type="button"
              className="mat-calendar-period-button mdc-button mat-mdc-button mat-unthemed mat-mdc-button-base"
              aria-live="polite"
              aria-label="Choose month and year"
              onClick={() => setView(view === 'month' ? 'multi-year' : 'month')}
            >
              <span className="mat-mdc-button-persistent-ripple mdc-button__ripple"></span>
              <span className="mdc-button__label">
                <span aria-hidden="true">{periodLabel}</span>
                <svg
                  className={`mat-calendar-arrow${view !== 'month' ? ' mat-calendar-invert' : ''}`}
                  viewBox="0 0 10 5"
                  focusable="false"
                  aria-hidden="true"
                >
                  <polygon points="0,0 5,5 10,0" />
                </svg>
              </span>
              <span className="mat-mdc-focus-indicator"></span>
              <span className="mat-mdc-button-touch-target"></span>
            </button>
          )}
          <div className="mat-calendar-spacer"></div>
          <button
            type="button"
            className="mat-calendar-previous-button mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
            aria-label={previousLabel}
            onClick={previous}
          >
            <span className="mat-mdc-button-persistent-ripple mdc-icon-button__ripple"></span>
            <span className="mat-mdc-focus-indicator"></span>
            <span className="mat-mdc-button-touch-target"></span>
          </button>
          <button
            type="button"
            className="mat-calendar-next-button mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
            aria-label={nextLabel}
            onClick={next}
          >
            <span className="mat-mdc-button-persistent-ripple mdc-icon-button__ripple"></span>
            <span className="mat-mdc-focus-indicator"></span>
            <span className="mat-mdc-button-touch-target"></span>
          </button>
        </div>
      </div>,
      <label className="mat-calendar-hidden-label" id={`${id}-period`}>
        {periodLabel}
      </label>,
    ),
    <div ref={focusRoot} tabIndex={-1} className="mat-calendar-content">
      {view === 'month' &&
        month &&
        createElement(
          'mat-month-view',
          {},
          <table role="grid" className="mat-calendar-table">
            <thead className="mat-calendar-table-header">
              <tr>
                {WEEKDAYS.map((name) => (
                  <th key={name} scope="col">
                    <span className="cdk-visually-hidden">{name}</span>
                    <span aria-hidden="true">{name.slice(0, 2)}</span>
                  </th>
                ))}
              </tr>
              <tr>
                <th aria-hidden="true" colSpan={7} className="mat-calendar-table-header-divider"></th>
              </tr>
            </thead>
            <CalendarBody
              label={MONTHS_SHORT[active.month()]}
              rows={month.rows}
              numCols={7}
              firstRowOffset={month.offset}
              labelMinRequiredCells={3}
              cellAspectRatio={1}
              onKeyDown={onKeyDown}
              onSelect={(cell) => {
                const date = active.date(cell.value);
                setActive(date);
                onSelect(date);
              }}
            />
          </table>,
        )}
      {view === 'year' &&
        createElement(
          'mat-year-view',
          {},
          <table role="grid" className="mat-calendar-table">
            <thead aria-hidden="true" className="mat-calendar-table-header">
              <tr>
                <th className="mat-calendar-table-header-divider" colSpan={4}></th>
              </tr>
            </thead>
            <CalendarBody
              label={String(active.year())}
              rows={yearCells()}
              numCols={4}
              labelMinRequiredCells={2}
              cellAspectRatio={4 / 7}
              onKeyDown={onKeyDown}
              onSelect={(cell) => {
                setActive(active.month(cell.value));
                setView('month');
              }}
            />
          </table>,
        )}
      {view === 'multi-year' &&
        createElement(
          'mat-multi-year-view',
          {},
          <table role="grid" className="mat-calendar-table">
            <thead aria-hidden="true" className="mat-calendar-table-header">
              <tr>
                <th className="mat-calendar-table-header-divider" colSpan={4}></th>
              </tr>
            </thead>
            <CalendarBody
              label=""
              rows={multiYearCells()}
              numCols={4}
              labelMinRequiredCells={3}
              cellAspectRatio={4 / 7}
              onKeyDown={onKeyDown}
              onSelect={(cell) => {
                setActive(active.year(cell.value));
                setView('year');
              }}
            />
          </table>,
        )}
    </div>,
  );
}
